import {
  DatePipe,
  NgClass,
  NgFor,
  NgIf,
  NgSwitch,
  NgSwitchCase,
  NgSwitchDefault,
  NgTemplateOutlet,
} from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { NgxPaginationModule } from "ngx-pagination";
import { Observable, Subscription, of } from "rxjs";
import { last, map, switchMap, tap } from "rxjs/operators";

import { IncidenciasService } from "src/app/incidencias/incidencias.service";
import { ProyectosService } from "src/app/proyectos/proyectos.service";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { Proyecto } from "src/app/shared/model/gestion-proyectos/proyecto";
import { EvidenciaBean } from "src/app/shared/model/incidencias/evidenciaBean.model";
import { TareaDiaria, TareaDiariaEvidencia } from "src/app/shared/model/tarea/tareaDiaria.model";
import { AzureService, RespuestaAzure, TareaAzure } from "src/app/shared/services/azure-devops.service";
import { EvidenciaViewerService } from "src/app/shared/services/evidencia-viewer.service";
import { GenericoService } from "src/app/shared/services/generico.service";
import { SweetAlertComponent } from "src/app/shared/SweetMesssage";
import { Constantes } from "src/app/utilerias/constantes/constantes";
import { UrlConstantes } from "src/app/utilerias/constantes/urlconstantes";

/** Lo que recibe el cuadro: sin tarea registra una nueva; con tarea muestra su detalle. */
export interface DatosTareaDialogo {
  tarea: TareaDiaria | null;
}

/** Un proyecto del usuario listo para el autocompletado de «Proyecto». */
export interface ProyectoTarea {
  /** Id del documento de Mongo (ObjectId en hexadecimal): es lo que se guarda en la tarea. */
  id: string;
  nombre: string;
  /** «idProyecto · cliente», la segunda línea de la opción. */
  detalle: string;
  fechaFin: Date | null;
  /** fechaFin ya pasó y el proyecto sigue ACTIVO: se puede elegir, pero se avisa. */
  vencido: boolean;
  idProyectoAzure: string | null;
  nombreProyectoAzure: string | null;
  /** Nombre, nombre corto, clave, idProyecto y cliente sin acentos ni mayúsculas. */
  busqueda: string;
}

type EstadoCarga = "cargando" | "listo" | "error";
type EstadoTareasAzure = "sin-liga" | EstadoCarga;

const ID_TITULO = "tarea-dialogo-titulo";
const ID_PROYECTO = "tarea-dialogo-proyecto";

/**
 * Tope del nombre. Es nuevo: el alta original no tenía ninguno (solo
 * Validators.required); el maxlength="50" estaba solo en el campo de solo
 * lectura del detalle. El DDL de cos_tarea_diaria no está en el repositorio;
 * si la columna admite más, se puede subir aquí.
 */
const LARGO_NOMBRE = 50;
const LARGO_DESCRIPCION = 250;
/**
 * Tope que pone solo el front. El servidor acepta hasta 500 MB: WebConfig
 * declara su propio MultipartConfigElement y las spring.servlet.multipart.*
 * de configGeneral.properties no se aplican.
 */
const TOPE_EVIDENCIAS_BYTES = 50 * 1024 * 1024;
const REGISTROS_HISTORIAL = 5;
/** Tipo de consulta de PoyectosBussines: proyectos vigentes del usuario para registrar tareas. */
const CONSULTA_MIS_PROYECTOS = 7;

const MENSAJE_SIN_PROYECTOS =
  "No tienes proyectos activos asignados. Pide a tu líder que te agregue al equipo.";

/**
 * Abre «Registrar tarea» (sin tarea) o el detalle de una tarea con la forma de
 * los diálogos estándar (app-standard-dialog, como la captura del BSC).
 * El detalle recibe una COPIA del renglón: el cuadro nunca toca la tabla.
 *
 * @returns el cuadro; afterClosed() da true si se guardó algo.
 */
export function abrirTareaDialogo(
  dialog: MatDialog,
  tarea: TareaDiaria | null
): MatDialogRef<TareaAltaComponent, boolean> {
  return dialog.open<TareaAltaComponent, DatosTareaDialogo, boolean>(TareaAltaComponent, {
    data: { tarea: tarea ? Object.assign(new TareaDiaria(), tarea) : null },
    width: "960px",
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 32px)",
    panelClass: "app-standard-dialog",
    backdropClass: "app-standard-dialog-backdrop",
    ariaLabelledBy: ID_TITULO,
    autoFocus: tarea ? "first-heading" : `#${ID_PROYECTO}`,
    disableClose: true,
  });
}

/** Minúsculas y sin acentos, para buscar «diseno» y encontrar «Diseño». */
export function normalizaTexto(texto: string | null | undefined): string {
  return (texto ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Proyectos que contienen TODAS las palabras escritas en cualquiera de sus
 * datos (nombre, nombre corto, clave, idProyecto o cliente).
 */
export function filtraProyectos(proyectos: ProyectoTarea[], texto: string): ProyectoTarea[] {
  const palabras = normalizaTexto(texto).split(/\s+/).filter(Boolean);
  if (palabras.length === 0) {
    return proyectos;
  }
  return proyectos.filter((p) => palabras.every((palabra) => p.busqueda.includes(palabra)));
}

/** fechaFin anterior a hoy (al inicio del día, hora local). Sin fecha no está vencido. */
export function estaVencido(fechaFin: Date | null, hoy: Date = new Date()): boolean {
  if (!fechaFin) {
    return false;
  }
  const inicioDeHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  return fechaFin.getTime() < inicioDeHoy.getTime();
}

/**
 * Fecha del backend (ISO con zona o milisegundos). Una fecha sin hora
 * ("2026-10-08") se toma como día local: new Date() la leería en UTC y en
 * México caería en el día anterior.
 */
function aFecha(valor: unknown): Date | null {
  if (valor === null || valor === undefined || valor === "") {
    return null;
  }
  if (typeof valor === "string" && /^\d{4}-\d{2}-\d{2}$/.test(valor)) {
    const [anio, mes, dia] = valor.split("-").map(Number);
    return new Date(anio, mes - 1, dia);
  }
  const fecha = new Date(valor as string | number | Date);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
}

/** dd/MM/yyyy, como el resto de la pantalla. */
function fechaCorta(fecha: Date): string {
  const dosDigitos = (n: number) => String(n).padStart(2, "0");
  return `${dosDigitos(fecha.getDate())}/${dosDigitos(fecha.getMonth() + 1)}/${fecha.getFullYear()}`;
}

function aProyectoTarea(p: Proyecto, hoy: Date): ProyectoTarea {
  const nombre = (p.nombre || p.nombre_corto || p.clave || "Proyecto sin nombre").trim();
  const fechaFin = aFecha(p.fechaFin);
  return {
    // El modelo lo tipa como number, pero en Mongo es un ObjectId (texto).
    id: String(p.id),
    nombre,
    detalle: [p.idProyecto || p.clave, p.nombreCliente].filter(Boolean).join(" · "),
    fechaFin,
    vencido: estaVencido(fechaFin, hoy),
    idProyectoAzure: p.idProyectoAzure || null,
    nombreProyectoAzure: p.nombreProyectoAzure || null,
    busqueda: normalizaTexto(
      [nombre, p.nombre_corto, p.clave, p.idProyecto, p.nombreCliente].filter(Boolean).join(" ")
    ),
  };
}

/** Exige que el autocompletado tenga un proyecto ELEGIDO, no solo texto escrito. */
function proyectoElegido(control: AbstractControl): ValidationErrors | null {
  const valor = control.value;
  return typeof valor === "string" && valor.trim() !== "" ? { proyectoNoElegido: true } : null;
}

/** Rechaza un texto hecho solo de espacios (required lo deja pasar). */
function conTexto(control: AbstractControl): ValidationErrors | null {
  const valor = control.value;
  return typeof valor === "string" && valor !== "" && valor.trim() === "" ? { required: true } : null;
}

/** pCodRetorno "00000": la operación se hizo. */
function esExito(codigo: unknown): boolean {
  return codigo !== null && codigo !== undefined && codigo !== "" && Number(codigo) === 0;
}

/** Títulos únicos y no vacíos de los work items de Azure, en el orden en que llegan. */
function titulosDeTareas(tareas: TareaAzure[]): string[] {
  const titulos = tareas
    .map((t) => (typeof t?.titulo === "string" ? t.titulo.trim() : ""))
    .filter(Boolean);
  return Array.from(new Set(titulos));
}

/** Error de negocio del alta: lleva el pMensaje del backend. */
class ErrorDeGuardado extends Error {}

/**
 * Tarea diaria · «Registrar tarea» y detalle de una tarea (los abre
 * tarea-consulta con abrirTareaDialogo).
 *
 * Alta: solo los proyectos vigentes del usuario (consulta tipo 7: ACTIVO, no
 * oportunidad, con el usuario en el equipo o como líder). Un proyecto ACTIVO
 * con fechaFin pasada se marca «Vencido» pero se puede elegir. Si el proyecto
 * está ligado a Azure DevOps, sus work items alimentan «Nombre de tarea».
 * Guardar espera a que suban las evidencias antes de cerrar; si el backend
 * rechaza la tarea, su mensaje se queda a la vista y el cuadro sigue abierto.
 *
 * Detalle: datos de la tarea, historial de tiempos y evidencias. El nombre
 * del proyecto llega con el historial; no consulta Azure ni la lista del alta.
 *
 * Escape, el fondo, la X y Cancelar pasan por cancelar(): con cambios sin
 * guardar piden una segunda pulsación para descartarlos.
 */
@Component({
  selector: "app-tarea-alta",
  standalone: true,
  templateUrl: "./tarea-alta.component.html",
  styleUrls: ["./tarea-alta.component.css"],
  imports: [
    NgIf,
    NgFor,
    NgClass,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    NgTemplateOutlet,
    DatePipe,
    ReactiveFormsModule,
    MatDialogContent,
    MatDialogActions,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    NgxPaginationModule,
  ],
})
export class TareaAltaComponent implements OnInit, OnDestroy {
  readonly idTitulo = ID_TITULO;
  readonly idProyecto = ID_PROYECTO;
  readonly largoNombre = LARGO_NOMBRE;
  readonly largoDescripcion = LARGO_DESCRIPCION;
  readonly registrosHistorial = REGISTROS_HISTORIAL;
  readonly mensajeSinProyectos = MENSAJE_SIN_PROYECTOS;

  /** Sin tarea de entrada es «Registrar tarea»; con tarea, su detalle. */
  readonly esAlta: boolean;

  /* ---------------------------- Alta ---------------------------- */
  readonly formulario = new FormGroup({
    proyecto: new FormControl<ProyectoTarea | string | null>(null, [
      Validators.required,
      proyectoElegido,
    ]),
    nombre: new FormControl("", {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(LARGO_NOMBRE), conTexto],
    }),
    descripcion: new FormControl("", {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(LARGO_DESCRIPCION), conTexto],
    }),
  });
  proyectos: ProyectoTarea[] = [];
  proyectosFiltrados: ProyectoTarea[] = [];
  /** Lo que va escrito en «Proyecto»; el control no lo recibe (ver alEscribirProyecto). */
  private textoProyecto = "";
  estadoProyectos: EstadoCarga = "cargando";
  totalVencidos = 0;
  tareasAzure: string[] = [];
  tareasAzureFiltradas: string[] = [];
  estadoTareasAzure: EstadoTareasAzure = "sin-liga";
  /** Lo que respondió el backend cuando Azure no contestó (sin token, caído…). */
  motivoTareasAzure = "";

  /* ---------------------------- Detalle ---------------------------- */
  readonly tarea: TareaDiaria | null;
  historial: any[] = [];
  estadoHistorial: EstadoCarga = "cargando";
  paginaHistorial = 1;
  tiempoInvertido = "";
  nombreProyecto = "";

  /* ---------------------------- Compartido ---------------------------- */
  /** Archivos elegidos que aún no se suben. */
  evidencias: File[] = [];
  guardando = false;
  /** El error, lo que falta o el aviso de descartar; vacío no se ve. */
  mensaje = "";
  /** Primer Cancelar con cambios: el segundo los descarta. */
  confirmarDescarte = false;
  /**
   * La tarea ya se registró pero sus evidencias no subieron: «Guardar» solo
   * reintenta la subida (no vuelve a dar de alta la tarea).
   */
  tareaGuardada: TareaDiaria | null = null;

  private readonly subs = new Subscription();
  private subTareasAzure: Subscription | null = null;
  private readonly cacheTareasAzure = new Map<string, string[]>();
  private idProyectoActual: string | null = null;
  private cerrado = false;

  constructor(
    private readonly dialogRef: MatDialogRef<TareaAltaComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) datos: DatosTareaDialogo,
    private readonly proyectoService: ProyectosService,
    private readonly azureService: AzureService,
    private readonly incidenciasService: IncidenciasService,
    private readonly service: GenericoService,
    private readonly sweetAlert: SweetAlertComponent,
    private readonly visorEvidencias: EvidenciaViewerService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.tarea = datos?.tarea ?? null;
    this.esAlta = this.tarea?.id === null || this.tarea?.id === undefined;
  }

  ngOnInit(): void {
    this.subs.add(this.dialogRef.backdropClick().subscribe(() => this.cancelar()));
    this.subs.add(
      this.dialogRef.keydownEvents().subscribe((ev) => {
        if (ev.key === "Escape") {
          ev.preventDefault();
          this.cancelar();
        }
      })
    );

    if (this.esAlta) {
      this.iniciaAlta();
    } else {
      this.iniciaDetalle();
    }
  }

  ngOnDestroy(): void {
    this.cerrado = true;
    this.subTareasAzure?.unsubscribe();
    this.subs.unsubscribe();
  }

  /* ============================ Alta: proyectos ============================ */

  get proyectoSeleccionado(): ProyectoTarea | null {
    const valor = this.formulario.controls.proyecto.value;
    return valor && typeof valor === "object" ? valor : null;
  }

  /** Lo que muestra el campo con un proyecto elegido. Flecha: Material la llama sin this. */
  readonly muestraProyecto = (valor: ProyectoTarea | string | null): string =>
    typeof valor === "string" ? valor : valor?.nombre ?? "";

  /** Debajo de «Proyecto»: cuántos hay o, con uno elegido, lo que conviene saber de él. */
  get ayudaProyecto(): string {
    if (this.estadoProyectos === "cargando") {
      return "Cargando tus proyectos…";
    }
    const elegido = this.proyectoSeleccionado;
    if (elegido?.vencido) {
      const fin = elegido.fechaFin ? ` el ${fechaCorta(elegido.fechaFin)}` : "";
      return `Venció${fin}. Puedes registrar la tarea; pide a tu líder que lo cierre o amplíe la fecha.`;
    }
    if (elegido?.idProyectoAzure) {
      const nombreAzure = elegido.nombreProyectoAzure ? `: ${elegido.nombreProyectoAzure}` : "";
      return `Ligado a Azure DevOps${nombreAzure}.`;
    }
    if (this.estadoProyectos === "error") {
      return "";
    }
    const total = this.proyectos.length;
    const activos = `${total} ${total === 1 ? "proyecto activo" : "proyectos activos"}`;
    if (this.totalVencidos > 0) {
      const vencidos = this.totalVencidos === 1 ? "vencido" : "vencidos";
      return `${activos} · ${this.totalVencidos} ${vencidos}`;
    }
    return activos;
  }

  trackProyecto(_: number, p: ProyectoTarea): string {
    return p.id;
  }

  /** Vuelve a pedir los proyectos después de un error. */
  cargaProyectos(): void {
    this.estadoProyectos = "cargando";
    const consulta = new ConsultaList<Proyecto>();
    // Sin param4: el backend toma el usuario de la sesión.
    consulta.param6 = CONSULTA_MIS_PROYECTOS;
    consulta.pagina = 0;
    consulta.regXPagina = Constantes.MIL;

    this.subs.add(
      this.proyectoService.consultaProyectoMongo(consulta).subscribe({
        next: (res: { content?: Proyecto[] } | null) => {
          // Un error del backend llega como 200 sin cuerpo: no es «sin proyectos».
          if (!res || !Array.isArray(res.content)) {
            this.estadoProyectos = "error";
          } else {
            this.ponProyectos(res.content);
            this.estadoProyectos = "listo";
          }
          this.pinta();
        },
        error: () => {
          this.estadoProyectos = "error";
          this.pinta();
        },
      })
    );
  }

  private iniciaAlta(): void {
    const controles = this.formulario.controls;
    this.subs.add(
      controles.proyecto.valueChanges.subscribe((valor) => {
        // Al elegir, o cuando requireSelection lo deja en null al cerrar la
        // lista, se vuelve a ofrecer la lista completa.
        this.textoProyecto = typeof valor === "string" ? valor : "";
        this.proyectosFiltrados = filtraProyectos(this.proyectos, this.textoProyecto);
        this.cambiaProyecto(valor && typeof valor === "object" ? valor : null);
        this.cambio();
      })
    );
    this.subs.add(
      controles.nombre.valueChanges.subscribe((valor) => {
        this.tareasAzureFiltradas = this.filtraTareasAzure(valor);
        this.cambio();
      })
    );
    this.subs.add(controles.descripcion.valueChanges.subscribe(() => this.cambio()));
    this.cargaProyectos();
  }

  private ponProyectos(lista: Proyecto[]): void {
    const hoy = new Date();
    // Las tareas de vacaciones las genera RH al aprobar la solicitud: no se registran aquí.
    this.proyectos = lista
      .filter((p) => p?.id !== null && p?.id !== undefined)
      .filter((p) => p.nombre !== Constantes.PROYECTO_VACACIONES)
      .map((p) => aProyectoTarea(p, hoy));
    this.totalVencidos = this.proyectos.filter((p) => p.vencido).length;
    // Respeta lo que se alcanzó a escribir mientras la lista cargaba.
    this.proyectosFiltrados = filtraProyectos(this.proyectos, this.textoProyecto);
  }

  /**
   * Filtra con lo escrito. Con requireSelection, Material no escribe en el
   * control mientras se teclea (valueChanges no se dispara hasta elegir o
   * cerrar la lista), así que el texto llega del (input), igual que en el
   * campo de Azure DevOps de la edición de proyecto.
   */
  alEscribirProyecto(texto: string): void {
    this.textoProyecto = texto ?? "";
    this.proyectosFiltrados = filtraProyectos(this.proyectos, this.textoProyecto);
    this.pinta();
  }

  /** Al elegir (o dejar de tener) proyecto se cargan o se quitan sus tareas de Azure. */
  private cambiaProyecto(proyecto: ProyectoTarea | null): void {
    const id = proyecto?.id ?? null;
    if (id === this.idProyectoActual) {
      return;
    }
    this.idProyectoActual = id;
    this.subTareasAzure?.unsubscribe();
    this.subTareasAzure = null;
    this.ponTareasAzure([], "sin-liga");
    if (proyecto?.idProyectoAzure) {
      this.cargaTareasAzure(proyecto.idProyectoAzure);
    }
  }

  /* ============================ Alta: tareas de Azure ============================ */

  private cargaTareasAzure(idProyectoAzure: string): void {
    const enCache = this.cacheTareasAzure.get(idProyectoAzure);
    if (enCache) {
      this.ponTareasAzure(enCache, "listo");
      return;
    }
    this.estadoTareasAzure = "cargando";
    this.subTareasAzure = this.azureService.getTareasProyectoAzure(idProyectoAzure).subscribe({
      next: (res: RespuestaAzure<TareaAzure[]> | null) => {
        if (res && esExito(res.codRet) && Array.isArray(res.data)) {
          const titulos = titulosDeTareas(res.data);
          this.cacheTareasAzure.set(idProyectoAzure, titulos);
          this.ponTareasAzure(titulos, "listo");
        } else {
          this.falloTareasAzure(res?.mensaje);
        }
        this.pinta();
      },
      error: (err) => {
        this.falloTareasAzure(err?.error?.mensaje);
        this.pinta();
      },
    });
  }

  /** Debajo de «Nombre de tarea»: de dónde salen las sugerencias o por qué no hay. */
  get ayudaNombre(): string {
    switch (this.estadoTareasAzure) {
      case "cargando":
        return "Buscando tus tareas activas en Azure DevOps…";
      case "error":
        return `No se pudieron traer las tareas de Azure DevOps${
          this.motivoTareasAzure ? ` (${this.motivoTareasAzure})` : ""
        }. Escribe el nombre.`;
      case "listo":
        return this.tareasAzure.length > 0
          ? "Elige una de tus tareas activas en Azure DevOps o escribe otro nombre."
          : "No tienes tareas activas en Azure DevOps para este proyecto.";
      default:
        return "";
    }
  }

  private ponTareasAzure(titulos: string[], estado: EstadoTareasAzure): void {
    this.tareasAzure = titulos;
    this.estadoTareasAzure = estado;
    this.motivoTareasAzure = "";
    this.tareasAzureFiltradas = this.filtraTareasAzure(this.formulario.controls.nombre.value);
  }

  private falloTareasAzure(motivo: string | undefined): void {
    this.ponTareasAzure([], "error");
    this.motivoTareasAzure = (motivo ?? "").trim();
  }

  private filtraTareasAzure(texto: string): string[] {
    const buscado = normalizaTexto(texto);
    return buscado
      ? this.tareasAzure.filter((t) => normalizaTexto(t).includes(buscado))
      : this.tareasAzure;
  }

  /* ============================ Alta: guardar ============================ */

  get puedeGuardar(): boolean {
    return !this.guardando && (this.tareaGuardada !== null || this.formulario.valid);
  }

  guardar(): void {
    if (this.guardando || this.cerrado) {
      return;
    }
    if (this.tareaGuardada) {
      // La tarea ya existe: solo falta reintentar las evidencias.
      this.ejecutaGuardado(this.subeEvidencias(this.tareaGuardada));
      return;
    }
    const proyecto = this.proyectoSeleccionado;
    if (this.formulario.invalid || !proyecto) {
      this.formulario.markAllAsTouched();
      this.mensaje = proyecto
        ? "Revisa los campos marcados."
        : "Elige uno de tus proyectos de la lista.";
      this.pinta();
      return;
    }

    const tarea = new TareaDiaria();
    tarea.nombre = this.formulario.controls.nombre.value.trim();
    tarea.descripcion = this.formulario.controls.descripcion.value.trim();
    tarea.idProyecto = proyecto.id;

    const alta$ = this.incidenciasService
      .obtenerElementoPorCampoPost(UrlConstantes.OPERACION_TAREA_DIARIA, tarea)
      .pipe(
        map((res: TareaDiaria | null) => {
          // Un error del backend también puede llegar como 200 sin cuerpo.
          if (!res || !esExito(res.pCodRetorno) || res.id === null || res.id === undefined) {
            throw new ErrorDeGuardado(res?.pMensaje || "No se pudo registrar la tarea. Intenta de nuevo.");
          }
          return res;
        }),
        tap((res) => (this.tareaGuardada = res)),
        switchMap((res) => this.subeEvidencias(res))
      );
    this.ejecutaGuardado(alta$);
  }

  /** Corre el alta o la subida y decide qué decir; el cuadro se cierra solo si todo salió. */
  private ejecutaGuardado(proceso$: Observable<unknown>): void {
    this.guardando = true;
    this.mensaje = "";
    this.confirmarDescarte = false;
    this.pinta();
    this.subs.add(
      proceso$.subscribe({
        next: () => {
          this.guardando = false;
          this.sweetAlert.showSwal("success-message", Constantes.VACIO);
          this.cierra(true);
        },
        error: (err) => {
          this.guardando = false;
          if (this.tareaGuardada) {
            // La tarea quedó registrada: el formulario ya no se edita, solo las evidencias.
            this.formulario.disable({ emitEvent: false });
            this.mensaje =
              "La tarea se registró, pero no se pudieron subir las evidencias. " +
              "Pulsa «Reintentar evidencias» o cierra para continuar sin ellas.";
          } else {
            this.mensaje =
              err instanceof ErrorDeGuardado
                ? err.message
                : "No se pudo registrar la tarea. Revisa tu conexión e intenta de nuevo.";
          }
          this.pinta();
        },
      })
    );
  }

  /* ============================ Evidencias ============================ */

  get textoEvidencias(): string {
    const guardadas = this.evidenciasGuardadas.length;
    const pendientes = this.evidencias.length;
    const partes: string[] = [];
    if (guardadas > 0) {
      partes.push(`${guardadas} ${guardadas === 1 ? "guardada" : "guardadas"}`);
    }
    if (pendientes > 0) {
      partes.push(`${pendientes} por subir (${this.tamanoArchivo(this.totalBytesEvidencias)})`);
    }
    return partes.length > 0 ? partes.join(" · ") : "Sin archivos";
  }

  get totalBytesEvidencias(): number {
    return this.evidencias.reduce((total, archivo) => total + archivo.size, 0);
  }

  agregaEvidencias(input: HTMLInputElement): void {
    const archivos = Array.from(input.files ?? []);
    input.value = "";
    const rechazados: string[] = [];
    let total = this.totalBytesEvidencias;
    for (const archivo of archivos) {
      const repetido = this.evidencias.some(
        (e) => e.name === archivo.name && e.size === archivo.size && e.lastModified === archivo.lastModified
      );
      if (repetido) {
        continue;
      }
      if (total + archivo.size > TOPE_EVIDENCIAS_BYTES) {
        rechazados.push(archivo.name);
        continue;
      }
      total += archivo.size;
      this.evidencias = [...this.evidencias, archivo];
    }
    this.cambio();
    if (rechazados.length > 0) {
      this.mensaje = `Las evidencias no pueden sumar más de 50 MB; no se agregó: ${rechazados.join(", ")}.`;
    }
    this.pinta();
  }

  /** Por posición: dos archivos con el mismo nombre ya no se confunden. */
  quitaEvidencia(indice: number): void {
    if (this.guardando) {
      return;
    }
    this.evidencias = this.evidencias.filter((_, i) => i !== indice);
    this.cambio();
  }

  iconoArchivo(nombre: string): string {
    return this.visorEvidencias.obtenIcono(nombre);
  }

  tamanoArchivo(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Sube los archivos pendientes de la tarea y emite una vez al terminar
   * (o de inmediato si no hay archivos). El backend separa el nombre por "$":
   * TAREA$QA$<idTarea>$<idProyecto>$<archivo>, así que un "$" en el nombre se cambia.
   */
  private subeEvidencias(tarea: TareaDiaria): Observable<unknown> {
    if (this.evidencias.length === 0) {
      return of(null);
    }
    const datos = new FormData();
    for (const archivo of this.evidencias) {
      const nombre = archivo.name.replaceAll("$", "_");
      datos.append("file", archivo, `TAREA$QA$${tarea.id}$${tarea.idProyecto}$${nombre}`);
    }
    // uploadEvidenciaTarea emite el progreso; solo interesa que termine.
    return this.incidenciasService.uploadEvidenciaTarea(datos).pipe(last(null, null));
  }

  /* ============================ Detalle ============================ */

  get estatusClase(): string {
    switch (this.tarea?.estatus) {
      case "TERMINADO":
      case "ACTIVO":
        return "is-active";
      case "PAUSADO":
      case "PENDIENTE":
        return "is-pending";
      default:
        return "is-inactive";
    }
  }

  get evidenciasGuardadas(): TareaDiariaEvidencia[] {
    return Array.isArray(this.tarea?.evidencias) ? this.tarea.evidencias : [];
  }

  get finHistorial(): number {
    return Math.min(this.paginaHistorial * REGISTROS_HISTORIAL, this.historial.length);
  }

  cambiaPaginaHistorial(pagina: number): void {
    this.paginaHistorial = pagina;
  }

  /** Vuelve a pedir el historial después de un error. */
  cargaHistorial(): void {
    if (!this.tarea) {
      return;
    }
    this.estadoHistorial = "cargando";
    this.subs.add(
      this.service.obtenerElementoPorCampoPost(UrlConstantes.OBTENER_TAREA_HISTORIAL, this.tarea).subscribe({
        next: (res: TareaDiaria | null) => {
          // El backend avisa sus errores con 200 y pCodRetorno distinto de
          // cero; si todo sale bien regresa el mismo cuerpo, casi siempre sin
          // código, así que solo un código presente y distinto de cero es error.
          const codigo = res?.pCodRetorno;
          const conError = codigo !== null && codigo !== undefined && codigo !== "" && !esExito(codigo);
          if (!res || conError) {
            this.estadoHistorial = "error";
          } else {
            this.historial = Array.isArray(res.historial) ? res.historial : [];
            this.tiempoInvertido = res.tiempoTotalInvertidoStr || this.tiempoInvertido;
            // Resuelto en el backend aunque el proyecto ya esté cerrado o el
            // usuario haya salido del equipo; null si el proyecto ya no existe.
            this.nombreProyecto = res.nombreProyecto || "";
            this.paginaHistorial = 1;
            this.estadoHistorial = "listo";
          }
          this.pinta();
        },
        error: () => {
          this.estadoHistorial = "error";
          this.pinta();
        },
      })
    );
  }

  guardaEvidencias(): void {
    if (this.guardando || this.cerrado || !this.tarea || this.evidencias.length === 0) {
      return;
    }
    this.guardando = true;
    this.mensaje = "";
    this.confirmarDescarte = false;
    this.pinta();
    this.subs.add(
      this.subeEvidencias(this.tarea).subscribe({
        next: () => {
          this.guardando = false;
          this.sweetAlert.showSwal("success-message", Constantes.VACIO);
          this.cierra(true);
        },
        error: () => {
          this.guardando = false;
          this.mensaje = "No se pudieron subir las evidencias. Intenta de nuevo.";
          this.pinta();
        },
      })
    );
  }

  verEvidencia(evidencia: TareaDiariaEvidencia): void {
    const bean = this.evidenciaDeTarea(evidencia);
    if (this.visorEvidencias.esImagen(bean.nombre)) {
      this.incidenciasService.condultaImagenEvidencia(bean).subscribe({
        next: (res) => this.visorEvidencias.abreImagen(bean.nombre, res),
        error: () => this.visorEvidencias.avisoNoDisponible(),
      });
      return;
    }
    if (this.visorEvidencias.esVideo(bean.nombre)) {
      this.service.consultaVideoEvidencia(UrlConstantes.consultaVideoEvidencia, bean).subscribe({
        next: (res) => this.visorEvidencias.abreVideo(bean.nombre, res),
        error: () => this.visorEvidencias.avisoNoDisponible(),
      });
      return;
    }
    // Excel, PDF, Word, comprimidos, etc. no se previsualizan: se descargan.
    this.descargaEvidencia(evidencia);
  }

  descargaEvidencia(evidencia: TareaDiariaEvidencia): void {
    this.service
      .descargaEvidencia(
        UrlConstantes.descargaEvidencia,
        "TAREA",
        this.tarea?.idProyecto,
        evidencia.id_tarea,
        evidencia.nombreArchivo
      )
      .subscribe({
        next: (res) => this.visorEvidencias.descarga(evidencia.nombreArchivo, res),
        error: () => this.visorEvidencias.avisoNoDisponible(),
      });
  }

  /** Lo que muestra «Proyecto» mientras no hay nombre. */
  get proyectoSinNombre(): string {
    if (this.estadoHistorial === "cargando") {
      return "Consultando…";
    }
    if (this.estadoHistorial === "error") {
      return "No se pudo consultar";
    }
    return this.tarea?.idProyecto ? "Proyecto no encontrado" : "Sin proyecto";
  }

  private iniciaDetalle(): void {
    this.tiempoInvertido = this.tarea?.tiempoTotalInvertidoStr || "";
    this.cargaHistorial();
  }

  /**
   * El modelo TS tipa EvidenciaBean.idProyecto como number, pero en Java es
   * String y los ids de Mongo son hexadecimales: Number() lo volvía NaN (null
   * en el JSON) y la previsualización fallaba.
   */
  private evidenciaDeTarea(evidencia: TareaDiariaEvidencia): EvidenciaBean {
    const bean = new EvidenciaBean();
    bean.idProyecto = this.tarea?.idProyecto as unknown as number;
    bean.idTarea = evidencia.id_tarea;
    bean.nombre = evidencia.nombreArchivo;
    return bean;
  }

  /* ============================ Cerrar ============================ */

  /** Algo que se perdería al cerrar. */
  get hayCambios(): boolean {
    if (this.evidencias.length > 0) {
      return true;
    }
    if (!this.esAlta) {
      return false;
    }
    const { proyecto, nombre, descripcion } = this.formulario.getRawValue();
    const conProyecto = typeof proyecto === "string" ? proyecto.trim() !== "" : proyecto !== null;
    return conProyecto || nombre.trim() !== "" || descripcion.trim() !== "";
  }

  cancelar(): void {
    if (this.guardando || this.cerrado) {
      return;
    }
    if (this.tareaGuardada) {
      // Ya se registró: lo único pendiente son evidencias que el usuario decide dejar.
      this.cierra(true);
      return;
    }
    if (this.hayCambios && !this.confirmarDescarte) {
      this.confirmarDescarte = true;
      this.mensaje = `Tienes cambios sin guardar. Vuelve a pulsar ${
        this.esAlta ? "Cancelar" : "Cerrar"
      } para descartarlos.`;
      this.pinta();
      return;
    }
    this.cierra(false);
  }

  private cierra(guardado: boolean): void {
    if (this.cerrado) {
      return;
    }
    this.cerrado = true;
    this.dialogRef.close(guardado);
  }

  /* ============================ Utilidades ============================ */

  /** Cada cambio borra el mensaje anterior (también el aviso de descartar). */
  private cambio(): void {
    if (!this.guardando && this.mensaje) {
      this.mensaje = "";
      this.confirmarDescarte = false;
    }
  }

  /** Lo que llega de una petición se pinta a mano (la app no depende de zonas). */
  private pinta(): void {
    this.cdr.markForCheck();
  }
}
